/* istanbul ignore file */
/* eslint-disable no-continue */

// GLOBAL IMPORTS
import pluralize from 'pluralize';
import { capitalize, isEmpty } from 'lodash';
import { applySnapshot, Instance, types, cast, clone } from 'mobx-state-tree';
import Router from 'next/router';
import { reaction, runInAction, toJS } from 'mobx';
import { getEnv } from 'mst-effect';
import { either } from 'fp-ts';
import { DeepPartial } from 'react-hook-form';
import { indexBy } from 'rambda';

// APP IMPORTS
import { Annotation, BaseResponse, Collaborator, Concept, ConceptRelation, Data, Input, StatusCode, User, Worker } from 'api/grpc/resources';
import { updateMST, withRunInAction } from 'utils/withRunInAction';
import { mapOr, pipe } from 'utils/fp';
import { postAnnotationsTE } from 'api/annotations';
import { AnnoUiFrameItem, AnnoUiRegionItem } from 'utils/cfUiItems/annotations';
import { dataIsConcepts, isBboxShapeProps } from 'utils/typeguards/predictionOutputs';
import { makeUID } from 'utils/strings/strings';
import {
  generateInternalAnnotationId,
  decomposeInternalAnnotationId,
  getConceptsFromAnnotationData,
  viewAnnotationList,
  updateAnnotationProperties,
} from 'utils/annotations/general';
import { ModelOutputSetting } from 'modules/Predictions/mst/WorkflowPredictionStore';
import { appStorage } from 'utils/appStorage';
import { AnnotationMST, TIAnnotationMST } from 'modules/Annotations/store/annotation';
import { grpcToRestAnnotation, grpcToRestConcept, grpcToRestConceptRelation } from 'utils/grpc/grpcToRest';
import { convertJsAnnotationToGrpcAnnotation, convertJsConceptToGrpcConcept } from 'utils/grpc/jsToGrpc';
import { updateAnnotationConcepts, updateConceptsFromAnnotationData } from 'utils/annotations/concept';
import cogoToast, { CTOptions } from 'components/ToastMessage/ToastMessage';
import {
  AnnotationsRequestProps,
  AnnotationsResponseProps,
  createVideoMaskModelRequest,
  groupFramedPointAnnotationsByConcept,
  getCreateAnnotationsRequest,
  getEditedAnnotationsReqest,
  getRemovedAnnotationsRequest,
  handleVideoMaskResponse,
  transformSuggestionsToAnnotations,
  convertVideoMasksToAnnotations,
  splitAnnotationsIntoChunks,
  VideoMask,
} from 'modules/Annotations/helpers/storeHelpers';
import { ModelOrWorkflow } from 'modules/InputViewer/components/Sidebar/Annotations/AnnotationSettings/AnnotationSettings';
import { MultiConceptRelationResponse } from 'api/grpc/service';
import { hasConcepts, hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { createValidConceptNameIdSet } from 'utils/concepts';
import {
  createTempClassificationAnnotation,
  determineRegionInfo,
  getClassificationMapEntryKey,
  getIsAnnotationTrusted,
  getRegionMapEntryKey,
  NON_TRUSTED_ANNOTATION_SEPARATOR,
  partitionAnnotationsByTrust,
} from 'modules/Annotations/store/helpers';
import { getAnnotationsFromConceptName, getConceptFromAnnotations } from 'modules/InputViewer/components/Sidebar/Annotations/utils';
import { getAIAssistRegionAnnotationConcept } from 'api-proxy/public/getAIAssistRegionAnnotationConcept';
import { createConcept } from 'api-proxy/public/createConcept';
import { FeatureFlagsType } from 'modules/FeatureFlagsLD/common';
import { SAVE_VIDEO_MASKS_BATCH_SIZE } from 'constants/videoTracking';

const ERROR_MESSAGE_NON_TRUSTED = 'Cannot update untrusted annotations. These may be pending approval in the original task.';
const ERROR_MESSAGE_NON_TRUSTED_PARTIALLY = 'Some of the annotations have not been updated. These may be pending approval in the original task.';
const checkIfResponseHasError = (response: FP.Either<Error, BaseResponse>) =>
  either.isLeft(response) || response?.right.getStatus()?.getCode() !== StatusCode.SUCCESS;

const RequestParamsModel = types.model('RequestBodyModel', {
  inputId: types.string,
  userId: types.string,
  appId: types.string,
  body: types.model({
    ids: types.array(types.string),
    input_ids: types.array(types.string),
  }),
});

export type RequestParamsType = Instance<typeof RequestParamsModel>;

type WorkerProps = {
  userId?: string;
  modelVersionId?: string;
  workflowVersionId?: string;
};

type ListAnnotationsProps = {
  taskId?: string;
  statuses?: StatusCode[];
} & WorkerProps;

type FetchWorkerAnnotationsProps = {
  input?: Input;
} & WorkerProps;

export enum AnnotationStatus {
  Any = '', // this is an empty esta, used as default in objects view
  Success = 'Success',
  AwaitingReview = 'Awaiting Review',
  AwaitingConsensusReview = 'Awaiting Consensus Review',
  Pending = 'Pending',
}

const AnnotationStatusModel = types.enumeration<AnnotationStatus>(Object.values(AnnotationStatus));

const LabelerModel = types.model('Labeler', {
  id: types.identifier,
  firstName: types.string,
  lastName: types.string,
});

const AnnotationFilterModel = types.model('AnnotationFilter', {
  labelerIds: types.optional(types.array(types.string), []),
  labelers: types.optional(types.array(LabelerModel), []),
  concepts: types.optional(types.array(types.frozen<Concept>()), []),
  annotationStatuses: types.optional(types.array(AnnotationStatusModel), []),
});

const ConceptRelationMapMSTType = types.optional(types.map(types.optional(types.array(types.frozen<CF.API.Concepts.ConceptRelation>()), [])), {});

// We create a new type instead of using the `Collaborator` interface because we also need to
// show the owner of the app as a labeler.
export type LabelerType = Instance<typeof LabelerModel>;

export type ClassificationMapEntryValue = {
  concept: CF.API.Concept;
  annotations: CF.API.Annotations.Annotation[];
};

export type RegionMapEntryValue = {
  concept: CF.API.Concept;
  regionMap: Map<string, CF.API.Annotations.Annotation[]>;
};

export type RegionAnnotationMap = Map<string, RegionMapEntryValue | null>;

export type ClassificationAnnotationMap = Map<string, CF.API.Annotations.Annotation[]>;

const requestConfig = {
  PER_PAGE: 250,
};

// Similar to what has been done in `annotationsStore`. Not ideal, but it's the only way we've found to share
// state between stores. This will probably go away once we merge both stores.
const createCanvasStoreModel = types.model({}).extend(() => {
  let selectedShape: string | null = null;
  let setSelectedShape: (value: string | null) => void;

  return {
    views: {
      getCanvasStoreState() {
        return {
          selectedShape,
          setSelectedShape,
        };
      },
    },
    actions: {
      setSelectedShape(value: string | null) {
        selectedShape = value;
      },
      setSelectedShapeFn(fn: typeof setSelectedShape) {
        setSelectedShape = fn;
      },
    },
  };
});

enum Action {
  CREATE = 'create',
  EDIT = 'edit',
  REMOVE = 'remove',
}

const taskAddSuccessStatus = (annotation: CF.API.Annotations.Annotation): CF.API.Annotations.Annotation => ({
  ...annotation,
  status: {
    code: StatusCode.ANNOTATION_SUCCESS,
    description: '',
  },
});

const taskCreateClassificationAnnotations = (concepts: Concept[], appId: string, userId: string, inputId: string): Annotation[] => {
  const annotationVirtualIds: string[] = [];
  const annotations: Annotation[] = [];
  concepts.forEach((concept) => {
    const crtAnnotationVirtualId = makeUID();
    const annotationData = new Data();
    const annotation = new Annotation();
    const worker = new Worker();
    const user = new User();

    concept.setAppId(appId);
    concept.setUserId(userId);
    concept.setValue(concept.getValue());

    annotationData.setConceptsList([concept]);

    annotation.setId(crtAnnotationVirtualId);
    annotation.setInputId(inputId);
    annotation.setData(annotationData);

    annotationVirtualIds.push(crtAnnotationVirtualId);

    // since this is created by user, we don't need model/workflow workers to be set
    if (userId) {
      user.setId(userId);
      worker.setUser(user);
      annotation.setWorker(worker);
    }

    annotations.push(annotation);
  });
  return annotations;
};

// TODO: Used in TaskManager and TaskLabelManager
export const AnnotationsStore = types
  .compose(
    withRunInAction(),
    createCanvasStoreModel,
    types
      .model({
        requestParams: types.optional(RequestParamsModel, {
          inputId: '',
          userId: '',
          appId: '',
          body: { ids: [''], input_ids: [''] },
        }),
        hasError: types.optional(types.boolean, false),
        hasShapeSaved: types.optional(types.boolean, true),
        annotationFilters: types.optional(AnnotationFilterModel, {}),
        isPredictionsLoading: types.optional(types.boolean, false),
        wasPredictionTriggered: types.optional(types.boolean, false),
        conceptRelationMap: ConceptRelationMapMSTType,
        workflowOrModel: types.maybeNull(types.frozen<CF.API.Workflows.Workflow | CF.API.Models.Model>()),
        isAiAssistantEnabled: types.optional(types.boolean, true),
        showCanvasNotifications: types.optional(types.boolean, false),
        conceptRelationsCache: types.optional(types.map(types.frozen<FP.Either<Error, MultiConceptRelationResponse>>()), {}),
        collobratorsListCache: types.optional(types.map(types.frozen<Collaborator[]>()), {}),

        moveOptionsPopupVisible: types.optional(types.boolean, false),
        annotationToMoveFrom: types.maybeNull(types.frozen<CF.API.Annotations.Annotation>()),

        data: types.array(AnnotationMST),

        predictionThreshold: types.maybe(types.number),

        // buffered items are for task manager related annotations
        taskCreatedAnnotationsBuffer: types.array(AnnotationMST),
        taskEditedAnnotationsBuffer: types.array(AnnotationMST),
        taskRemovedAnnotationsBuffer: types.array(AnnotationMST),
        taskDisableRegionConceptIds: types.map(types.array(types.string)),
        taskAcceptedSuggestions: types.optional(types.array(types.string), []),

        annotationSearchText: types.optional(types.string, ''),

        // Classification annotation pane data structures.
        classificationPaneConcepts: types.optional(types.array(types.frozen<Concept>()), []),
        classificationAnnotationMap: types.optional(types.map(types.array(AnnotationMST)), {}),

        // Region annotation pane data structures.
        regionPaneConcepts: types.optional(types.array(types.frozen<Concept>()), []),
        regionAnnotationMap: types.optional(types.map(types.frozen<RegionMapEntryValue | null>()), {}),
        isVideoMasksLoading: types.optional(types.boolean, false),
        videoMasks: types.optional(types.array(types.frozen<VideoMask>()), []),
      })
      .named('AnnotationListStore'),
    types.model({
      // TODO: used in both InputViewer and TaskViewer
      ModelOutputSettings: types.map(ModelOutputSetting),
    }),
  )
  .views((self) => ({
    // TODO: used in TaskManagerStore and TaskLabelManagerStore
    env() {
      return getEnv(self).env as CF.MSTEnvironment;
    },
  }))
  .views((self) => ({
    get _areAnnotationFiltersEmpty() {
      return (
        !self.annotationFilters.concepts.length && !self.annotationFilters.labelerIds.length && !self.annotationFilters.annotationStatuses.length
      );
    },
  }))
  .views((self) => ({
    get currentAnnotationStatuses(): StatusCode[] {
      if (self.annotationFilters.annotationStatuses.includes(AnnotationStatus.Any)) {
        return [];
      }
      const statusMap = {
        [AnnotationStatus.Success]: StatusCode.ANNOTATION_SUCCESS,
        [AnnotationStatus.AwaitingReview]: StatusCode.ANNOTATION_AWAITING_REVIEW,
        [AnnotationStatus.AwaitingConsensusReview]: StatusCode.ANNOTATION_AWAITING_CONSENSUS_REVIEW,
        [AnnotationStatus.Pending]: StatusCode.ANNOTATION_PENDING,
        [AnnotationStatus.Any]: undefined,
      };

      return self.annotationFilters.annotationStatuses.map((status) => statusMap[status] || undefined).filter((status) => !!status);
    },
  }))
  .views((self) => ({
    get _filteredAnnotations() {
      const { labelerIds, concepts, annotationStatuses } = self.annotationFilters;
      let result: TIAnnotationMST[] = self.data;

      if (self._areAnnotationFiltersEmpty) {
        return self.data;
      }

      if (labelerIds.length) {
        result = result.filter((annotation) => labelerIds.some((labelerId) => annotation.worker?.user?.id === labelerId));
      }

      if (concepts.length) {
        const conceptsByName = concepts.reduce<Record<string, Concept>>((conceptMap, concept) => {
          conceptMap[concept.getName()] = concept;

          return conceptMap;
        }, {});

        result = result.filter((annotation) => {
          const annotationConcepts = getConceptsFromAnnotationData(annotation.data);

          return annotationConcepts.some((annotationConcept) => conceptsByName[annotationConcept.name]);
        });
      }

      if (annotationStatuses.length && !annotationStatuses.includes(AnnotationStatus.Any)) {
        const annotationStatusCodeMap = {
          [AnnotationStatus.Success]: StatusCode.ANNOTATION_SUCCESS,
          [AnnotationStatus.AwaitingReview]: StatusCode.ANNOTATION_AWAITING_REVIEW,
          [AnnotationStatus.AwaitingConsensusReview]: StatusCode.ANNOTATION_AWAITING_CONSENSUS_REVIEW,
          [AnnotationStatus.Pending]: StatusCode.ANNOTATION_PENDING,
          [AnnotationStatus.Any]: '',
        };

        result = result.filter((annotation) =>
          annotationStatuses.some((annotationStatus) => annotationStatusCodeMap[annotationStatus] === annotation.status?.code),
        );
      }

      return result;
    },
  }))
  .views((self) => ({
    get annotationsFromClassificationMap() {
      return Array.from(self.classificationAnnotationMap.values()).flat();
    },

    get annotationsFromRegionMap() {
      return [...self.regionAnnotationMap.values()].flatMap((mapEntry) => (mapEntry ? [...mapEntry.regionMap.values()].flat() : []));
    },
  }))
  .views((self) => ({
    get maskedAnnotations() {
      if (!self._filteredAnnotations) {
        return [];
      }

      return toJS(self._filteredAnnotations).filter((annotation: CF.API.Annotations.Annotation) => {
        const data = annotation?.data ? (annotation.data as CF.API.BaseRegionsData) : null;
        if (data?.regions && data.regions.length > 0) {
          return data.regions.some((region: CF.API.RegionsEntity) => {
            const regionInfo = region.region_info as CF.API.ImageMaskRegionInfo;
            return regionInfo?.mask?.image?.url;
          });
        }
        return false;
      });
    },
  }))
  .actions((self) => ({
    _getLoggedInUserId() {
      const authData = appStorage.get('authData') as CF.LocalUser;
      return authData?.user_id;
    },

    setConceptRelationsCache(cacheKey: string, value: FP.Either<Error, MultiConceptRelationResponse>) {
      self.conceptRelationsCache.set(cacheKey, value);
    },

    getConceptRelationsCache(cacheKey: string) {
      return self.conceptRelationsCache.get(cacheKey);
    },

    setCollaboratorsCache(cacheKey: string, value: Collaborator[]) {
      self.collobratorsListCache.set(cacheKey, value);
    },

    getCollaboratorsCache(cacheKey: string) {
      return self.collobratorsListCache.get(cacheKey);
    },

    setClassificationPaneConcepts(newConcepts: Concept[]) {
      self.classificationPaneConcepts = cast(newConcepts);
    },

    appendClassificationPaneConcepts(...conceptsToAppend: Concept[]) {
      self.classificationPaneConcepts = cast([...self.classificationPaneConcepts, ...conceptsToAppend]);
    },

    removeClassificationPaneConcept(conceptToRemove: Concept) {
      self.classificationPaneConcepts = cast(self.classificationPaneConcepts.filter((concept) => concept.getId() !== conceptToRemove.getId()));
    },

    setRegionPaneConcepts(newConcepts: Concept[]) {
      self.regionPaneConcepts = cast(newConcepts);
    },

    appendRegionPaneConcepts(...conceptsToAppend: Concept[]) {
      self.regionPaneConcepts = cast([...self.regionPaneConcepts, ...conceptsToAppend]);
    },

    removeRegionPaneConcept(conceptToRemove: Concept) {
      self.regionPaneConcepts = cast(self.regionPaneConcepts.filter((concept) => concept.getId() !== conceptToRemove.getId()));
    },

    setAnnotationSearchText(searchText: string) {
      self.annotationSearchText = searchText;
    },

    async _fetchOrCreateConceptInApp(concept: CF.API.ConceptPartial) {
      const { appId, userId } = self.requestParams;
      const { grpc } = self.env();

      try {
        const getConceptEither = await grpc.getConcept(
          {
            appId,
            userOrOrgId: userId,
            conceptId: concept.id,
          },
          false,
        );
        const fetchedConcept = mapOr(getConceptEither, (response) => response.getConcept(), null);

        if (fetchedConcept && fetchedConcept.getAppId() === appId) {
          return fetchedConcept;
        }

        // If the concept does not exist, we create it.
        const response = await createConcept({
          request: {
            userOrOrgId: userId,
            appId,
          },
          concept: {
            name: concept.name,
            app_id: appId,
          },
        });

        if (response && response.concept && response.concept?.id && response.concept?.name) {
          return convertJsConceptToGrpcConcept(response.concept as CF.API.ConceptPartial);
        }
        return null;
      } catch {
        return null;
      }
    },
    setIsPredictionsLoading(loading: boolean) {
      self.isPredictionsLoading = loading;
    },
  }))
  .views((self) => ({
    get _myAnnotations() {
      const currentUserId = self._getLoggedInUserId();
      return self._filteredAnnotations.filter((annotation) => !isEmpty(annotation.data) && annotation.worker?.user?.id === currentUserId);
    },
  }))
  .views((self) => ({
    get myClassificationAnnotations() {
      return self._myAnnotations.filter((annotation) => dataIsConcepts(annotation.data));
    },
  }))
  .views((self) => ({
    get uiItemsMap() {
      return viewAnnotationList(self._filteredAnnotations);
    },
  }))
  .views((self) => ({
    get regionList() {
      return self.uiItemsMap.bbox.concat(self.uiItemsMap.polygon, self.uiItemsMap.imagemask);
    },
    get videoRegionList(): AnnoUiFrameItem<TIAnnotationMST>[] {
      return self.uiItemsMap.frame;
    },
  }))
  .views((self) => ({
    get hasClassificationAnnotations() {
      return self.uiItemsMap.concept.length > 0;
    },
    get hasRegionAnnotations() {
      return self.regionList.length > 0;
    },
  }))
  .views((self) => ({
    taskGetRegionCheckedStatus: (annotationInternalId?: string) => {
      if (!annotationInternalId) return false;
      const annotationData = decomposeInternalAnnotationId(annotationInternalId);
      const annotation = self.data.find(({ id }) => id === annotationData.id);
      if (!annotation?.isSuggestion) {
        return true;
      }
      return self.taskAcceptedSuggestions.includes(annotationInternalId);
    },
  }))
  .actions((self) => ({
    isClassificationConceptPinned(conceptId: string) {
      return Array.from(self.classificationAnnotationMap.values())
        .flat()
        .some((annotation) => {
          if (hasConcepts(annotation.data)) {
            return annotation.data.concepts?.some((c) => c.isPinned && c.id === conceptId);
          }

          return false;
        });
    },

    getPinnedClassificationConcepts() {
      const pinnedConcepts = Array.from(self.classificationAnnotationMap.values())
        .flat()
        .reduce<Concept[]>((result, annotation) => {
          if (hasConcepts(annotation.data)) {
            const concepts = annotation.data.concepts?.filter((c) => c.isPinned);

            if (concepts) {
              result = result.concat(concepts.map(convertJsConceptToGrpcConcept));
            }
          }

          return result;
        }, []);

      return pinnedConcepts;
    },

    hydrateClassificationsWithPinnedConcepts(pinnedConcepts: CF.API.Concept[]) {
      pinnedConcepts.forEach((concept) => {
        if (this.isClassificationConceptPinned(concept.id)) return;

        const tempAnnotation = createTempClassificationAnnotation({
          ...concept,
          isPinned: true,
        });

        if (tempAnnotation) {
          const currentAnnotations: CF.API.Annotations.Annotation[] = self.classificationAnnotationMap.get(concept.name) || [];

          self.classificationAnnotationMap.set(concept.name, currentAnnotations.concat(tempAnnotation));
        }
      });
    },

    updateClassificationMapOnPinRemoval(removedPinnedConcepts: CF.API.Concept[]) {
      removedPinnedConcepts.forEach((concept) => {
        if (!this.isClassificationConceptPinned(concept.id)) return;

        const annotations = self.classificationAnnotationMap.get(concept.name) || [];
        const updatedAnnotations = annotations.map((annotation) => {
          if (!hasConcepts(annotation.data)) return annotation;

          const concepts = (annotation.data.concepts || []).map((c) => {
            if (c.id === concept.id) {
              return { ...c, isPinned: false };
            }

            return c;
          });
          const updatedAnnotation = updateAnnotationConcepts(annotation, concepts);

          return updatedAnnotation || annotation;
        });

        self.classificationAnnotationMap.set(concept.name, updatedAnnotations);
      });
    },

    getPinnedRegionConcepts() {
      return Array.from(self.regionAnnotationMap.values()).reduce<Concept[]>((acc, mapEntry) => {
        if (!mapEntry) return acc;

        if (mapEntry.concept.isPinned) {
          acc.push(convertJsConceptToGrpcConcept(mapEntry.concept));
        }

        return acc;
      }, []);
    },

    addRegionAnnotationEntry(selectedConcept: Concept) {
      const concept = grpcToRestConcept(selectedConcept);

      if (!self.regionAnnotationMap.has(concept.name)) {
        self.regionAnnotationMap.set(concept.name, {
          concept,
          regionMap: new Map(),
        });
      }
    },

    removeRegionAnnotationEntry(concept: CF.API.Concept) {
      const mapEntry = self.regionAnnotationMap.get(concept.name);

      if (!mapEntry) return;

      const { regionMap } = mapEntry;

      const hasRealAnnotations = Array.from(regionMap.values()).some((regionAnnotations) => {
        return regionAnnotations.some((annotation) => !annotation.isSuggestion && !annotation.isTemporary);
      });

      if (hasRealAnnotations) return;

      self.regionAnnotationMap.delete(concept.name);
    },

    hydrateRegionsWithPinnedConcepts(concepts: CF.API.Concept[]) {
      for (const concept of concepts) {
        const pinnedConcept = { ...concept, isPinned: true };
        const key = pinnedConcept.name;
        const mapEntry = self.regionAnnotationMap.get(key);

        self.regionAnnotationMap.set(key, {
          concept: pinnedConcept,
          regionMap: mapEntry ? mapEntry.regionMap : new Map(),
        });
      }
    },

    updateRegionMapOnPinRemoval(removedPinnedConcepts: CF.API.Concept[]) {
      removedPinnedConcepts.forEach((pinnedConcept) => {
        const key = pinnedConcept.name;
        const mapEntry = self.regionAnnotationMap.get(key);

        if (!mapEntry) return;

        const { concept, regionMap } = mapEntry;

        if (concept.id !== pinnedConcept.id) return;

        self.regionAnnotationMap.set(key, { concept: { ...pinnedConcept, isPinned: false }, regionMap });
      });
    },

    setMoveOptionsPopupVisible(visible: boolean) {
      self.moveOptionsPopupVisible = visible;
    },

    handleSuccessToastNotification(type: string, message: string, options?: CTOptions): null | void {
      if (!self.showCanvasNotifications) {
        return null;
      }

      switch (type) {
        case 'success':
          return cogoToast.success(message, options);

        case 'error':
          return cogoToast.error(message, options);

        case 'info':
          return cogoToast.info(message, options);

        default:
          return cogoToast.info(message, options);
      }
    },
    setAiAssistantEnabled(isAiAssistantEnabled: boolean) {
      self.isAiAssistantEnabled = isAiAssistantEnabled;
    },
    _setData(annotations: CF.API.Annotations.Annotation[]): void {
      self.data = cast(annotations);
    },
    _taskSetEditedAnnotationsBuffer(annotations: CF.API.Annotations.Annotation[]): void {
      self.taskEditedAnnotationsBuffer = cast(annotations);
    },
    _taskSetCreatedAnnotationsBuffer(annotations: CF.API.Annotations.Annotation[]): void {
      self.taskCreatedAnnotationsBuffer = cast(annotations);
    },
    _taskSetRemovedAnnotationsBuffer(annotations: CF.API.Annotations.Annotation[]): void {
      self.taskRemovedAnnotationsBuffer = cast(annotations.map((annotation) => clone(annotation)));
    },
    taskSetDisableRegionConceptIds(conceptId: string, regionIds: string[]) {
      // Toggle the presence of `id` in `taskDisableRegionConceptIds`

      if (regionIds.length === 1) {
        const concept = self.taskDisableRegionConceptIds.get(conceptId) || ([] as string[]);
        const regionId = regionIds[0];
        if (concept.includes(regionId)) {
          self.taskDisableRegionConceptIds.set(
            conceptId,
            concept.filter((id) => id !== regionId),
          );
          return;
        }
        self.taskDisableRegionConceptIds.set(conceptId, concept.length ? [...concept, regionId] : [regionId]);
        return;
      }

      self.taskDisableRegionConceptIds.set(conceptId, regionIds);
    },
    taskRemoveDisabledRegionConceptIds() {
      self.taskDisableRegionConceptIds = cast({});
    },
  }))
  .actions((self) => ({
    appendAnnotationOptimistically(annotation: CF.API.Annotations.Annotation) {
      self._setData([...self.data, annotation]);
    },

    updateAnnotationOptimistically(updatedAnnotation: CF.API.Annotations.Annotation) {
      self._setData(
        self.data.map((annotation) => {
          if (annotation.id === updatedAnnotation.id) {
            return updatedAnnotation;
          }

          return annotation;
        }),
      );
    },

    removeAnnotationOptimistically(annotationToRemove: CF.API.Annotations.Annotation) {
      self._setData(self.data.filter((annotation) => annotation.id !== annotationToRemove.id));
    },
  }))
  .actions((self) => ({
    async postAnnotation(annotation: CF.API.Annotations.Annotation): Promise<CF.API.Annotations.Annotation[]> {
      try {
        const { appId, userId } = self.requestParams;
        const response = await self.env().grpc.postAnnotations({
          appId,
          userOrOrgId: userId,
          annotations: [convertJsAnnotationToGrpcAnnotation(annotation)],
        });

        return pipe(
          response,
          either.fold(
            () => [],
            (data) => data.getAnnotationsList().map(grpcToRestAnnotation) as CF.API.Annotations.Annotation[],
          ),
        );
      } catch {
        return [];
      }
    },

    async patchAnnotation(
      annotation: CF.API.Annotations.Annotation,
      action: 'overwrite' | 'merge' | 'remove' = 'overwrite',
    ): Promise<CF.API.Annotations.Annotation[]> {
      try {
        const { appId, userId } = self.requestParams;
        const response = await self.env().grpc.patchAnnotations({
          appId,
          userOrOrgId: userId,
          annotations: [convertJsAnnotationToGrpcAnnotation(annotation)],
          action,
        });

        return pipe(
          response,
          either.fold(
            () => [],
            (data) => data.getAnnotationsList().map(grpcToRestAnnotation) as CF.API.Annotations.Annotation[],
          ),
        );
      } catch {
        return [];
      }
    },
  }))
  .actions((self) => ({
    _rollbackAnnotations(annotationBackupArray: CF.API.Annotations.Annotation[], errorMessage: string) {
      self._setData(annotationBackupArray);
      cogoToast.error(errorMessage);
    },
    updateShowNotificationsStatus(show: boolean) {
      self.showCanvasNotifications = show;
    },
    taskResetAnnotationsBuffers(): void {
      self._taskSetEditedAnnotationsBuffer([]);
      self._taskSetCreatedAnnotationsBuffer([]);
      self._taskSetRemovedAnnotationsBuffer([]);
    },
    _taskAddEditedAnnotationBuffer(editedAnnotation: CF.API.Annotations.Annotation): void {
      const annotation = taskAddSuccessStatus(editedAnnotation);
      if (self.taskEditedAnnotationsBuffer.length === 0) {
        self._taskSetEditedAnnotationsBuffer([annotation]);
      } else {
        self.taskEditedAnnotationsBuffer.push(annotation);
      }
    },
    _taskAddCreatedAnnotationBuffer(createdAnnotation: CF.API.Annotations.Annotation): void {
      const annotation = taskAddSuccessStatus(createdAnnotation);

      if (self.taskCreatedAnnotationsBuffer.length === 0) {
        self._taskSetCreatedAnnotationsBuffer([annotation]);
      } else {
        self.taskCreatedAnnotationsBuffer.push(annotation);
      }
    },
    _taskAddRemovedAnnotationBuffer(annotation: CF.API.Annotations.Annotation): void {
      if (self.taskRemovedAnnotationsBuffer.length === 0) {
        self._taskSetRemovedAnnotationsBuffer([annotation]);
      } else {
        self.taskRemovedAnnotationsBuffer.push(clone(annotation));
      }
    },
  }))
  .actions((self) => ({
    _taskUpdateAnnotationsBuffer(action: Action, annotation: CF.API.Annotations.Annotation): void {
      const isCreatedAnnotation = self.taskCreatedAnnotationsBuffer.find((a) => a.id === annotation.id);
      const isEditedAnnotation = self.taskEditedAnnotationsBuffer.find((a) => a.id === annotation.id);
      const foundEditedAnnotation = self.taskEditedAnnotationsBuffer.find((a) => a.id === annotation.id);
      const foundDataAnnotation = self.data.find((a) => a.id === annotation.id);

      switch (action) {
        case Action.EDIT:
          if (isCreatedAnnotation) {
            self._taskSetCreatedAnnotationsBuffer(self.taskCreatedAnnotationsBuffer.map((a) => (a.id === annotation.id ? annotation : a)));
            break;
          }
          if (isEditedAnnotation) {
            self._taskSetEditedAnnotationsBuffer(self.taskEditedAnnotationsBuffer.map((a) => (a.id === annotation.id ? annotation : a)));
            break;
          }
          self._taskAddEditedAnnotationBuffer(annotation);
          break;
        case Action.REMOVE:
          if (isCreatedAnnotation) {
            self._taskSetCreatedAnnotationsBuffer(self.taskCreatedAnnotationsBuffer.filter((a) => a.id !== annotation.id));
            break;
          }
          if (isEditedAnnotation) {
            if (foundEditedAnnotation) {
              self._taskAddRemovedAnnotationBuffer(foundEditedAnnotation);
              self._taskSetEditedAnnotationsBuffer(self.taskEditedAnnotationsBuffer.filter((a) => a.id !== annotation.id));
            }
            break;
          }
          // Case it was defined in data
          if (foundDataAnnotation && !foundDataAnnotation.isSuggestion) {
            self._taskAddRemovedAnnotationBuffer(foundDataAnnotation);
            self._setData(self.data.filter((a) => a.id !== annotation.id));
          }
          break;
        default:
          break;
      }
    },
  }))
  .actions((self) => ({
    setSuggestions({
      inputId,
      suggestions,
      taskId,
      model,
      workflow,
    }: {
      inputId: string;
      suggestions: (CF.API.ModelOutputs.ModelOutput | CF.API.WorkflowOutputs.WorkflowOutput)[];
      taskId?: string;
      workflow?: CF.API.Workflows.Workflow;
      model?: CF.API.Models.Model;
    }): void {
      self.runInAction(() => {
        const suggestionArray = transformSuggestionsToAnnotations({
          inputId,
          suggestions,
          taskId,
          model,
          workflow,
        });
        self._setData(self.data.concat(suggestionArray));
      });
    },
    resetSuggestions() {
      self.runInAction(() => {
        self._setData(self.data.filter((annotation) => !annotation.isSuggestion));
      });
    },
    setIsPredictionsLoading(isPredictionsLoading: typeof self.isPredictionsLoading) {
      self.isPredictionsLoading = cast(isPredictionsLoading);
    },
    setPredictionThreshold(predictionThreshold: typeof self.predictionThreshold) {
      self.predictionThreshold = predictionThreshold;
    },
    setWasPredictionTriggered(wasPredictionTriggered: typeof self.wasPredictionTriggered) {
      self.wasPredictionTriggered = wasPredictionTriggered;
    },
    _setConceptRelationMap(conceptRelationMap: typeof self.conceptRelationMap) {
      self.conceptRelationMap = conceptRelationMap;
    },
  }))
  .actions((self) => ({
    taskSetAcceptedSuggestionsList(annotationInternalIds: string[]) {
      self.taskAcceptedSuggestions.replace(annotationInternalIds);
    },
    taskAddAcceptedSuggestion(annotationInternalId?: string) {
      if (annotationInternalId) self.taskAcceptedSuggestions.push(annotationInternalId);
    },
    taskRemoveAcceptedSuggestion(annotationInternalId?: string) {
      if (annotationInternalId) self.taskAcceptedSuggestions.replace(self.taskAcceptedSuggestions.filter((id) => id !== annotationInternalId));
    },
    taskResetSuggestionsList() {
      self.taskAcceptedSuggestions.clear();
    },
  }))
  .actions((self) => ({
    // We use the concept name instead of the ID because the ID is appended with random characters.
    _addConceptRelation(subjectConceptName: string, conceptRelation: CF.API.Concepts.ConceptRelation) {
      const relations: CF.API.Concepts.ConceptRelation[] = self.conceptRelationMap.get(subjectConceptName) || [];
      const relationFound = relations.find((relation) => relation.id === conceptRelation.id);

      if (!relationFound) {
        self.conceptRelationMap.set(subjectConceptName, relations.concat([conceptRelation]));
      }
    },
  }))
  .actions((self) => ({
    initConceptRelationMap(conceptRelations: CF.API.Concepts.ConceptRelation[]) {
      conceptRelations.forEach((conceptRelation) => {
        const { subjectConcept } = conceptRelation;

        if (subjectConcept) {
          self._addConceptRelation(subjectConcept.name, conceptRelation);
        }
      });
    },
  }))
  .actions((self) => ({
    taskInitConceptRelationsMapGrpc(conceptRelations: ConceptRelation[]) {
      self.initConceptRelationMap(conceptRelations.map(grpcToRestConceptRelation));
    },
    _addToList(list: CF.API.Annotations.Annotation[]): void {
      const existingIds = new Set(self.data.map((annotation) => annotation.id));

      const annotationsList = list
        .filter((annotation) => !existingIds.has(annotation.id))
        .map((annotation) => ({
          ...annotation,
          data: updateConceptsFromAnnotationData(annotation.data, (concepts) =>
            concepts.map((concept) => ({
              ...concept,
              name: concept.name.replaceAll(' ', '-'),
            })),
          ),
        }));

      self.data.push(...annotationsList);
    },
    taskGetRegionConceptValueByAnnotationId(annotationId: string): number | undefined {
      const currentAnnotation = self.data.find(({ id }) => id === annotationId) as CF.API.Annotations.Annotation;
      const regions = (currentAnnotation?.data as CF.API.BaseRegionsData)?.regions || [];
      const concepts = (regions[0]?.data as CF.API.BaseConceptsData)?.concepts || [];
      if (concepts?.length) {
        return concepts[0]?.value;
      }
      return undefined;
    },
    updateItems(items?: CF.API.Annotations.Annotation[]) {
      if (!items) {
        return;
      }
      const byId = indexBy((x) => x.id, items as CF.API.Annotations.Annotation[]);
      self.data.some((item) => {
        if (item.id in byId) {
          applySnapshot(item, byId[item.id]);
          delete byId[item.id];
        }

        if (!Object.keys(byId).length) return true;

        return false;
      });
    },
    setHasError(hasError: boolean) {
      self.hasError = hasError;
    },
    _setShapeSaved: (saved: boolean) => {
      self.hasShapeSaved = saved;
    },
    setLabelerFilters: (...labelers: LabelerType[]) => {
      self.annotationFilters = {
        ...self.annotationFilters,
        labelers: cast(labelers),
        labelerIds: cast(labelers.map(({ id }) => id)),
      };
    },
    setConceptFilters: (...concepts: Concept[]) => {
      self.annotationFilters = {
        ...self.annotationFilters,
        concepts: cast(concepts),
      };
    },
    setStatusFilters: (...annotationStatuses: AnnotationStatus[]) => {
      self.annotationFilters = {
        ...self.annotationFilters,
        annotationStatuses: cast(annotationStatuses),
      };
    },
    setWorkflowFilters: (workflowOrModel: ModelOrWorkflow | null) => {
      self.workflowOrModel = workflowOrModel;
    },
    setResetFilters: () => {
      self.annotationFilters = cast({
        labelerIds: [],
        concepts: [],
        annotationStatuses: [],
      });
    },
  }))
  .actions((self) => ({
    _unshiftData(annotations: CF.API.Annotations.Annotation[]): void {
      self.runInAction(() => {
        self._setData([...self.data, ...annotations]);
      });
    },
  }))
  .actions((self) => ({
    _removeAnnotation(annotationToRemove: CF.API.Annotations.Annotation) {
      const newData = self.data.filter((annotation) => annotation.id !== annotationToRemove.id);

      self._setData(newData);
    },
    _removeAnnotations(annotationsToRemove: CF.API.Annotations.Annotation[]) {
      self._setData(self.data.filter((annotation) => !annotationsToRemove.some(({ id }) => annotation.id === id)));
    },
  }))
  .actions((self) => ({
    async _listAnnotationsGrpc({
      userId,
      modelVersionId,
      workflowVersionId,
      page,
      taskId,
      statuses = [StatusCode.ANNOTATION_SUCCESS],
    }: ListAnnotationsProps & { page: number }): Promise<Annotation[] | null> {
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string };
      const listAnnotationsRequest = {
        userOrOrgId,
        appId,
        page,
        perPage: requestConfig.PER_PAGE,
        inputIds: self.requestParams.inputId ? [self.requestParams.inputId] : undefined,
        taskId,
        userIds: userId ? [userId] : undefined,
        modelVersionIds: modelVersionId ? [modelVersionId] : undefined,
        workflowVersionIds: workflowVersionId ? [workflowVersionId] : undefined,
        statuses,
      };
      const response = await self.env().grpc.listAnnotations(listAnnotationsRequest);
      const result = mapOr(response, (r) => r.getAnnotationsList(), null);
      return result;
    },
  }))
  .actions((self) => ({
    setRequestParams: (
      params: Partial<{
        inputId: string;
        userId: string;
        appId: string;
        body: {
          ids: string[];
          input_ids: string[];
        };
      }>,
    ) => {
      if (!Object.values(params).some((i) => i === undefined)) {
        applySnapshot(self.requestParams, params as unknown as typeof self.requestParams);
      }
    },
    async listAnnotations({ userId, modelVersionId, workflowVersionId, taskId, statuses }: ListAnnotationsProps = {}) {
      updateMST(self, () => {
        const annotationsByInputId = self.data.filter((annotations) => annotations.input_id === self.requestParams.inputId);
        self._setData(annotationsByInputId);
      });

      async function fetchPage(page = 1): Promise<void> {
        const response = await self._listAnnotationsGrpc({ userId, modelVersionId, workflowVersionId, taskId, page, statuses });
        if (response === null) {
          // TODO: handle error (previously error was not used in the view)
        } else {
          const annotations = response.map((a) => grpcToRestAnnotation(a)).filter((a) => !!a) as CF.API.Annotations.Annotation[];
          if (annotations.length) self._addToList(annotations);
          if (annotations.length === requestConfig.PER_PAGE) {
            await fetchPage(page + 1);
          }
        }
      }

      // run recursive process to fetch all annotations
      // TODO: isBusy is not used in the view
      // self.isBusy = true;
      await fetchPage();
    },
  }))
  .actions((self) => ({
    async createAnnotations(batchOfAnnotations: DeepPartial<CF.API.Annotations.Annotation & { status: Partial<CF.API.Status> }>[]) {
      // TODO move to gRPC
      const response = await postAnnotationsTE(self.requestParams.userId, self.requestParams.appId, {
        annotations: batchOfAnnotations,
      })();

      updateMST(self, () => {
        pipe(
          response,
          either.fold(
            (res) => showCreationError(res.status),
            (res) => {
              if (res.status.code === 10000) {
                self.data.unshift(...res.annotations);
                self.handleSuccessToastNotification('success', 'The annotation was created successfully.');
              } else {
                showCreationError(res.status);
              }
              return null;
            },
          ),
        );
      });
    },
  }))
  .actions((self) => ({
    taskCreateRegionsAnnotationLocally(...annotations: CF.API.Annotations.Annotation[]) {
      self._unshiftData(annotations);
      self._setShapeSaved(false);

      for (const annotation of annotations) {
        self._taskAddCreatedAnnotationBuffer(annotation);
      }
    },
    taskPatchBoundingBoxAnnotationLocally(item: AnnoUiRegionItem<TIAnnotationMST>, boundingBox?: CF.API.BoundingBox) {
      const loggedInUserId = self._getLoggedInUserId();
      const annotationList: CF.API.Annotations.Annotation[] = [];

      self._setData(
        self.data.map((annotation) => {
          if (item.annotation?.id && annotation.id === item.annotation.id) {
            const annotationData = item.annotation.data as CF.API.BaseRegionsData;
            const annotationToUpdate: CF.API.Annotations.Annotation = {
              ...item.annotation,
              id: item.annotation.id,
              internalId: item.annotation.internalId,
              worker: {
                user: {
                  id: loggedInUserId,
                } as CF.API.Users.User,
              },
              data: {
                ...annotationData,
                regions: (annotationData.regions || []).map((region) => {
                  if (item.key.includes(region.id)) {
                    return {
                      ...region,
                      region_info: {
                        bounding_box: boundingBox || (item.regionData as CF.API.BboxShapeProps).bbox,
                      },
                    };
                  }

                  return region;
                }),
              },
            };

            annotationList.push(annotationToUpdate);

            return annotationToUpdate;
          }

          return annotation;
        }),
      );

      for (const annotation of annotationList) {
        self._taskUpdateAnnotationsBuffer(Action.EDIT, annotation);
      }
    },
    taskPatchPolygonAnnotationLocally(item: AnnoUiRegionItem<TIAnnotationMST>, points?: CF.API.Polygon['points']) {
      const loggedInUserId = self._getLoggedInUserId();
      const annotationList: CF.API.Annotations.Annotation[] = [];

      self._setData(
        self.data.map((annotation) => {
          if (item.annotation?.internalId && annotation.id === decomposeInternalAnnotationId(item.annotation.internalId).id) {
            const annotationData = item.annotation.data as CF.API.BaseRegionsData;
            const annotationToUpdate: CF.API.Annotations.Annotation = {
              ...item.annotation,
              id: annotation.id,
              internalId: item.annotation.internalId,
              worker: {
                user: {
                  id: loggedInUserId,
                } as CF.API.Users.User,
              },
              data: {
                ...annotationData,
                regions: (annotationData.regions || []).map((region) => {
                  if (region.id === item.key) {
                    return {
                      ...region,
                      region_info: {
                        polygon: {
                          points: points || (item.regionData as CF.API.PolygonShapeProps).polygon.points,
                        },
                      },
                    };
                  }

                  return region;
                }),
              },
            };

            annotationList.push(annotationToUpdate);

            return annotationToUpdate;
          }

          return annotation;
        }),
      );

      for (const annotation of annotationList) {
        self._taskUpdateAnnotationsBuffer(Action.EDIT, annotation);
      }
    },
    taskDeleteRegionAnnotationLocally(annotation: CF.API.Annotations.Annotation) {
      const annotationToRemoveFromData = self.data.find((a) => a.id === annotation.id);

      if (annotationToRemoveFromData) {
        self._taskUpdateAnnotationsBuffer(Action.REMOVE, annotationToRemoveFromData);
        self._removeAnnotation(annotationToRemoveFromData);
      } else {
        self._taskUpdateAnnotationsBuffer(Action.REMOVE, annotation);
        self._removeAnnotation(annotation);
      }
    },
    taskPatchRegionAnnotationConceptValueLocally(oldAnnotation: CF.API.Annotations.Annotation, value: number) {
      const loggedInUserId = self._getLoggedInUserId();
      const annotationList: CF.API.Annotations.Annotation[] = [];
      self._setData(
        self.data.map((annotation) => {
          if (oldAnnotation && oldAnnotation.id === annotation.id) {
            const annotationToUpdate: CF.API.Annotations.Annotation = {
              ...annotation,
              worker: {
                user: {
                  id: loggedInUserId,
                } as CF.API.Users.User,
              },
              data: {
                ...annotation.data,
                regions:
                  (annotation.data as CF.API.BaseRegionsData)?.regions?.map((region) => ({
                    ...region,
                    data: {
                      ...region.data,
                      concepts:
                        (region.data as CF.API.BaseConceptsData)?.concepts?.map((concept) => ({
                          ...concept,
                          value,
                        })) || [],
                    },
                  })) || [],
              },
            };
            annotationList.push(annotationToUpdate);
            return annotationToUpdate;
          }
          return annotation;
        }),
      );

      for (const annotation of annotationList) {
        self._taskUpdateAnnotationsBuffer(Action.EDIT, annotation);
      }
    },
    taskPatchRegionAnnotationConceptLocally(oldAnnotation: CF.API.Annotations.Annotation, newConcept: Concept) {
      const loggedInUserId = self._getLoggedInUserId();
      const annotationList: CF.API.Annotations.Annotation[] = [];
      self._setData(
        self.data.map((annotation) => {
          if (oldAnnotation && oldAnnotation.id === annotation.id) {
            const annotationToUpdate: CF.API.Annotations.Annotation = {
              ...annotation,
              worker: {
                user: {
                  id: loggedInUserId,
                } as CF.API.Users.User,
              },
              data: {
                ...annotation.data,
                regions:
                  (annotation.data as CF.API.BaseRegionsData)?.regions?.map((region) => ({
                    ...region,
                    data: {
                      ...region.data,
                      concepts: [
                        {
                          ...grpcToRestConcept(newConcept),
                          value: 1,
                        },
                      ],
                    },
                  })) || [],
              },
            };
            annotationList.push(annotationToUpdate);
            return annotationToUpdate;
          }
          return annotation;
        }),
      );

      for (const annotation of annotationList) {
        self._taskUpdateAnnotationsBuffer(Action.EDIT, annotation);
      }
    },
  }))
  .actions((self) => ({
    taskCreateAnnotationFromSuggestion(annotation: CF.API.Annotations.Annotation, concept: Concept, inputId: string, taskId: string) {
      const { regionId: newRegionId, conceptId: newConceptId } = decomposeInternalAnnotationId(annotation.internalId);

      const isAlreadyAdded = self.taskCreatedAnnotationsBuffer.some((a) => {
        const { regionId, conceptId } = decomposeInternalAnnotationId(a.internalId);
        return regionId === newRegionId && conceptId === newConceptId;
      });
      if (isAlreadyAdded) return;

      const currentUserId = self._getLoggedInUserId();
      const { appId } = self.requestParams;
      const currentRegion = (annotation.data as CF.API.BaseRegionsData)?.regions?.[0];
      const regionInfo = currentRegion ? (currentRegion?.region_info as CF.API.RegionInfo) : undefined;
      const boundingBox = regionInfo && 'bounding_box' in regionInfo ? regionInfo.bounding_box : undefined;

      if (!boundingBox) {
        return;
      }

      const { top_row, left_col, bottom_row, right_col } = boundingBox;

      const annotationId = makeUID(16);
      const regionId = currentRegion?.id || makeUID(16);
      const conceptId = concept.getId();
      const worker = { user: { id: currentUserId } as CF.API.Users.User };
      const internalId = generateInternalAnnotationId({
        id: annotationId,
        regionId,
        conceptId,
        worker,
        taskId,
      });

      const annotationToCreate: CF.API.Annotations.Annotation = {
        id: annotationId,
        input_id: inputId,
        internalId,
        data: {
          regions: [
            {
              id: regionId,
              region_info: {
                bounding_box: {
                  top_row,
                  left_col,
                  bottom_row,
                  right_col,
                },
              },
              data: {
                concepts: [
                  {
                    id: conceptId,
                    name: concept.getName(),
                    app_id: appId,
                    user_id: currentUserId,
                    value: 1,
                  },
                ],
              },
            },
          ],
        },
        worker,
        isSuggestion: false,
        isTemporary: false,
        trusted: true,
        task_id: taskId,
      };

      self.taskCreateRegionsAnnotationLocally(annotationToCreate);
      self.taskAddAcceptedSuggestion(annotation.internalId);
    },
  }))
  .actions((self) => ({
    async _taskProcessAnnotations({
      buffer,
      getRequestFn,
    }: {
      buffer: TIAnnotationMST[];
      getRequestFn: (props: AnnotationsRequestProps) => AnnotationsResponseProps;
    }) {
      if (buffer.length === 0) return true;
      const { appId, userId } = self.requestParams;
      const request = getRequestFn({ annotations: buffer, appId, userId });
      const response = await self.env().grpc.patchAnnotations(request);
      const result = mapOr(response, () => {}, null);
      return result !== null;
    },
  }))
  .actions((self) => ({
    async _taskProcessLocallyCreatedAnnotations() {
      const annotations = self.taskCreatedAnnotationsBuffer;

      if (annotations.length === 0) return true;
      const { appId, userId } = self.requestParams;
      const { router } = Router;
      const { taskId } = router?.query as { taskId?: string };
      const currentUserId = self._getLoggedInUserId();
      const createAnnotationsRequest = getCreateAnnotationsRequest({
        annotations,
        appId,
        userId,
        currentUserId,
        taskId,
        statusCode: StatusCode.ANNOTATION_AWAITING_REVIEW,
      });

      const response = await self.env().grpc.postAnnotations(createAnnotationsRequest);
      const result = mapOr(response, () => {}, null);
      return result !== null;
    },
    async _taskProcessLocallyEditedAnnotations() {
      return self._taskProcessAnnotations({
        buffer: self.taskEditedAnnotationsBuffer,
        getRequestFn: getEditedAnnotationsReqest,
      });
    },
    async _taskProcessLocallyRemovedAnnotations() {
      return self._taskProcessAnnotations({
        buffer: self.taskRemovedAnnotationsBuffer,
        getRequestFn: getRemovedAnnotationsRequest,
      });
    },
  }))
  .actions((self) => ({
    async taskProcessLabelAnotaitonBuffers() {
      const isCreatedAnnotationsSuccess = await self._taskProcessLocallyCreatedAnnotations();

      if (!isCreatedAnnotationsSuccess) {
        cogoToast.error('There was an error while submitting annotations.');
        throw new Error('Failed to process annotation buffers');
      }

      self.handleSuccessToastNotification('success', 'Successfully submitted annotations!');
      self.taskResetSuggestionsList();
      return isCreatedAnnotationsSuccess;
    },
    async taskProcessAnnotationsBuffers() {
      const isCreatedAnnotationsSuccess = await self._taskProcessLocallyCreatedAnnotations();
      const isEditedAnnotationsSuccess = await self._taskProcessLocallyEditedAnnotations();
      const isRemovedAnnotationsSuccess = await self._taskProcessLocallyRemovedAnnotations();

      const isSuccess = isCreatedAnnotationsSuccess && isEditedAnnotationsSuccess && isRemovedAnnotationsSuccess;

      if (isSuccess) {
        self.handleSuccessToastNotification('success', 'Successfully submitted annotations!');
      } else {
        cogoToast.error('There was an error while submitting annotations.');
      }

      return isSuccess;
    },
    async taskProcessLabelTaskClassifications(
      concepts: Concept[],
      selectedClassifications: string[],
      taskId: string | undefined,
      inputId?: string | null,
    ) {
      const value = 1;
      const { appId, userId } = self.requestParams;
      const conceptToCrate = concepts.filter((concept) => selectedClassifications.some((selectedConcept) => selectedConcept === concept.getId()));
      conceptToCrate.forEach((concept) => {
        concept.setValue(value);
      });
      if (selectedClassifications.length && inputId && taskId) {
        const annotations = taskCreateClassificationAnnotations(conceptToCrate, appId, userId, inputId);
        const restAnnotations = annotations.map((a) => grpcToRestAnnotation(a)) as CF.API.Annotations.Annotation[];

        const currentUserId = self._getLoggedInUserId();
        try {
          const createAnnotationsRequest = getCreateAnnotationsRequest({
            annotations: restAnnotations,
            appId,
            userId,
            currentUserId,
            taskId,
            statusCode: StatusCode.ANNOTATION_AWAITING_REVIEW,
          });
          const createAnnotationsResponse = await self.env().grpc.postAnnotations(createAnnotationsRequest);
          pipe(
            createAnnotationsResponse,
            either.fold(
              () => {
                cogoToast.error('Error while creating annotation(s).');
              },
              (res) => {
                if (res?.getStatus()?.getCode() === 10000) {
                  self.handleSuccessToastNotification('success', 'Annotation(s) was created successfully.');
                } else {
                  cogoToast.error('Error while creating annotation(s).');
                }
              },
            ),
          );
        } catch (error) {
          console.error('Error creating annotations:', error);
          throw error;
        }
      }
    },
    async taskProcessClassificationAnnotations(concepts: Concept[], positiveConcepts: string[], negativeConcepts: string[], inputId?: string | null) {
      if (!inputId) return;
      const conceptsToKeep = concepts.filter((concept) => positiveConcepts.includes(concept.getId()) || negativeConcepts.includes(concept.getId()));
      const currentClassificationsList = self.data.filter((annotation) => dataIsConcepts(annotation.data)).map((d) => toJS(d));
      const currentClassificationsConceptsIds = currentClassificationsList.map((annotation) => getConceptsFromAnnotationData(annotation.data)[0].id);

      const classificationsToRemove = currentClassificationsList.filter(
        (annotation) =>
          // If value is 0, that means we learn from the labeling data
          // Meaning that the classification is marked as NOT particular concept through labeling
          // So such classification annotation should stay, and will be proceed by Back End
          !conceptsToKeep.some((concept) => concept.getId() === getConceptsFromAnnotationData(annotation.data)[0].id),
      );

      const { appId, userId } = self.requestParams;
      if (classificationsToRemove.length) {
        const removeAnnotationsRequest = getRemovedAnnotationsRequest({ annotations: classificationsToRemove, appId, userId });
        const removeAnnotationsResponse = await self.env().grpc.patchAnnotations(removeAnnotationsRequest);
        pipe(
          removeAnnotationsResponse,
          either.fold(
            () => {
              cogoToast.error('Error while removing annotation(s).');
            },
            (res) => {
              if (res?.getStatus()?.getCode() === 10000) {
                self.handleSuccessToastNotification('success', 'Annotation(s) was removed successfully.');
              } else {
                cogoToast.error('Error while removing annotation(s).');
              }
            },
          ),
        );
      }

      const classificationsToCreate = conceptsToKeep.filter((concept) => !currentClassificationsConceptsIds.includes(concept.getId()));

      if (classificationsToCreate.length) {
        const annotations = taskCreateClassificationAnnotations(classificationsToCreate, appId, userId, inputId);
        const restAnnotations = annotations.map((a) => grpcToRestAnnotation(a)) as CF.API.Annotations.Annotation[];

        const currentUserId = self._getLoggedInUserId();

        const createAnnotationsRequest = getCreateAnnotationsRequest({
          annotations: restAnnotations,
          appId,
          userId,
          currentUserId,
          statusCode: StatusCode.ANNOTATION_AWAITING_REVIEW,
        });
        const createAnnotationsResponse = await self.env().grpc.postAnnotations(createAnnotationsRequest);

        pipe(
          createAnnotationsResponse,
          either.fold(
            () => {
              cogoToast.error('Error while creating annotation(s).');
            },
            (res) => {
              if (res?.getStatus()?.getCode() === 10000) {
                self.handleSuccessToastNotification('success', 'Annotation(s) was created successfully.');
              } else {
                cogoToast.error('Error while creating annotation(s).');
              }
            },
          ),
        );
      }
      const classificationsToUpdate = currentClassificationsList.filter(
        (annotation) =>
          // If value is 0, that means we learn from the labeling data
          // Meaning that the classification is marked as NOT particular concept through labeling
          // So such classification annotation should stay, and will be proceed by Back End
          getConceptsFromAnnotationData(annotation.data)[0].value === 0 &&
          conceptsToKeep.some((concept) => concept.getId() === getConceptsFromAnnotationData(annotation.data)[0].id),
      );
      if (classificationsToUpdate.length) {
        const updatedClassifications = classificationsToUpdate.map((annotation) =>
          convertJsAnnotationToGrpcAnnotation({
            ...annotation,
            data: {
              ...annotation.data,
              concepts:
                (annotation?.data as CF.API.BaseConceptsData)?.concepts?.map((concept) => ({
                  ...concept,
                  value: 1,
                })) || [],
            },
          }),
        );
        const updateAnnotationsResponse = await self.env().grpc.patchAnnotations({
          appId,
          userOrOrgId: userId,
          annotations: updatedClassifications,
          action: 'overwrite',
        });
        pipe(
          updateAnnotationsResponse,
          either.fold(
            () => {
              cogoToast.error('Error while updating annotation(s).');
            },
            (res) => {
              if (res?.getStatus()?.getCode() === 10000) {
                self.handleSuccessToastNotification('success', 'Annotation(s) was updated successfully.');
              } else {
                cogoToast.error('Error while updating annotation(s).');
              }
            },
          ),
        );
      }
    },
  }))
  .actions((self) => ({
    taskFetchWorkerAnnotationsForInput({ input, userId, modelVersionId, workflowVersionId }: FetchWorkerAnnotationsProps) {
      const inputId = input?.getId();
      if (!inputId) return;
      const { router } = Router;
      const { userOrOrgId, appId, taskId } = router?.query as { userOrOrgId: string; appId: string; taskId: string };
      self.setRequestParams({
        appId,
        inputId,
        userId: userOrOrgId,
        body: { ids: [], input_ids: [] },
      });
      self._setData([]);
      self.listAnnotations({
        userId,
        modelVersionId,
        workflowVersionId,
        taskId,
        statuses: [StatusCode.ANNOTATION_SUCCESS, StatusCode.ANNOTATION_AWAITING_REVIEW],
      });

      self.taskResetAnnotationsBuffers();
    },
  }))
  .actions((self) => ({
    // Here we are passing inputId to the function and not accessing it directly from the router
    // because due to some reason when input was changing the correct inputId was not being updated through requestParams
    // TODO: Deprecate!
    async createRegionAnnotationOptimistically(...annotations: CF.API.Annotations.Annotation[]) {
      const { appId, userId } = self.requestParams;
      const annotationsWithSuccessStatus = annotations.map((annotation) => ({ ...annotation, trusted: true } as CF.API.Annotations.Annotation));

      self._unshiftData(annotationsWithSuccessStatus);
      const currentUserId = self._getLoggedInUserId();
      const createAnnotationsRequest = getCreateAnnotationsRequest({ annotations: annotationsWithSuccessStatus, appId, userId, currentUserId });
      self._setShapeSaved(false);
      const response = await self.env().grpc.postAnnotations(createAnnotationsRequest);
      self._setShapeSaved(true);

      pipe(
        response,
        either.fold(
          () => {
            self.setHasError(true);
            self._removeAnnotations(annotations);
            cogoToast.error('There was an error while creating the annotation.');
          },
          () => {
            self.handleSuccessToastNotification('success', 'The region annotation has been added successfully!');
          },
        ),
      );
    },
    async createRegionAnnotationsOptimistically(...annotations: CF.API.Annotations.Annotation[]) {
      // We backup the current annotation array for rollback in case of an error.
      const annotationBackupArray = clone(self.data);

      const { appId, inputId, userId: userOrOrgId } = self.requestParams;
      const userId = self._getLoggedInUserId();
      const updatedAnnotations: CF.API.Annotations.Annotation[] = [];

      for (const annotation of annotations) {
        // since we are creating new annotation, we need to provide new id for it
        const id = makeUID(16);
        const keyValuesToUpdate = {
          id,
          input_id: inputId,
          trusted: true,
          isSuggestion: false,
          isTemporary: false,
        };

        const updatedAnnotation: CF.API.Annotations.Annotation = {
          ...updateAnnotationProperties(annotation, keyValuesToUpdate),
          // overwrite worker to clear model and workflow, to keep only user
          worker: {
            user: {
              id: userId,
            } as CF.API.Users.User,
          },
        };

        const regions = (updatedAnnotation.data as CF.API.BaseRegionsData)?.regions || [];
        const newRegions = [] as CF.API.RegionsEntity[];
        const request = { userOrOrgId, appId };

        for (const region of regions) {
          const concepts = region.data.concepts || ([] as CF.API.Concept[]);
          const newConcepts = [] as CF.API.Concept[];

          for (const concept of concepts) {
            const aiAppId = annotation.worker?.workflow?.app_id || annotation.worker?.model?.app_id;
            const aiUserId = annotation.worker?.workflow?.user_id || annotation.worker?.model?.user_id;

            if (!!aiAppId && !!aiUserId) {
              const aiConcept = {
                id: concept.id,
                name: concept.name,
                appId: aiAppId,
                userId: aiUserId,
              };

              const newConcept = await getAIAssistRegionAnnotationConcept({ request, aiConcept });
              if (newConcept && newConcept?.id && newConcept?.name) {
                newConcepts.push({
                  id: newConcept.id,
                  name: newConcept.name,
                  value: 1,
                  app_id: newConcept.app_id,
                  user_id: newConcept.user_id,
                });
              } else {
                console.error('New Concept does not have id or name');
                cogoToast.error('New Concept does not have id or name');
                return;
              }
            } else {
              newConcepts.push(concept);
            }
          }

          newRegions.push({
            ...region,
            data: {
              ...region.data,
              concepts: newConcepts,
            },
          });
        }

        // Injecting new regions with new concept(s)
        updatedAnnotations.push({
          ...updatedAnnotation,
          data: {
            ...updatedAnnotation.data,
            regions: newRegions,
          },
        });
      }

      self._setData([...self.data, ...updatedAnnotations]);
      const pluralizedAnnotation = pluralize('annotation', annotations.length);
      const ERROR_MESSAGE = `There was an error while creating the ${pluralizedAnnotation}.`;

      const sentAnnotationsList = updatedAnnotations.map(convertJsAnnotationToGrpcAnnotation);

      try {
        const response = await self.env().grpc.postAnnotations({
          appId,
          userOrOrgId,
          annotations: sentAnnotationsList,
        });

        pipe(
          response,
          either.fold(
            () => self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE),
            () => {
              self.handleSuccessToastNotification('success', `${capitalize(pluralizedAnnotation)} successfully created.`);
            },
          ),
        );
      } catch {
        self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE);
      }
    },
  }))
  .actions((self) => ({
    resetAnnotationMaps() {
      self.classificationAnnotationMap.clear();
      self.regionAnnotationMap.clear();
    },

    hasTempClassificationAnnotation(concept: CF.API.Concept) {
      const conceptName = concept.name;
      const annotations = self.classificationAnnotationMap.get(conceptName) || [];

      return annotations.find((annotation) => annotation.isTemporary);
    },

    addTempClassificationAnnotation(concept: CF.API.Concept) {
      if (this.hasTempClassificationAnnotation(concept)) return;

      const conceptName = concept.name;
      const annotations = self.classificationAnnotationMap.get(conceptName) || [];
      const tempAnnotation = createTempClassificationAnnotation(concept);

      if (tempAnnotation) {
        self.classificationAnnotationMap.set(conceptName, [...annotations, tempAnnotation]);
      }
    },
    removeTempClassificationAnnotation(concept: CF.API.Concept) {
      const matchingAnnotations = getAnnotationsFromConceptName(concept.name, Array.from(self.classificationAnnotationMap.values()).flat());
      const realAnnotationExists = matchingAnnotations.find((annotation) => !annotation.isTemporary && !annotation.isSuggestion);

      if (realAnnotationExists) return;

      const suggestionAnnotations = (self.classificationAnnotationMap.get(concept.name) || []).filter((annotation) => !annotation.isTemporary);

      self.classificationAnnotationMap.set(concept.name, suggestionAnnotations);
    },
    async createClassificationAnnotationOptimistically(...concepts: Concept[]) {
      const { appId, userId, inputId } = self.requestParams;
      const currentUserId = self._getLoggedInUserId();
      const annotationVirtualIds: string[] = [];
      const annotationMap: Record<string, CF.API.Annotations.Annotation> = {};

      for (const currentConcept of concepts) {
        const crtAnnotationVirtualId = makeUID(16);

        // The idea behind this array is to keep track of the annotations that are being created.
        // If by any chance there's an error when creating them, we can remove them from the store.
        annotationVirtualIds.push(crtAnnotationVirtualId);

        if (currentConcept) {
          annotationMap[currentConcept.getId()] = {
            id: crtAnnotationVirtualId,
            data: { concepts: [currentConcept.toObject()] },
            input_id: inputId,
            worker: {
              user: {
                id: currentUserId,
              } as CF.API.Users.User,
            },
            // Since the user is creating the annotation, we can set it as trusted.
            // TODO as well as this trused and below success status is also a bad idea for taskReview page
            trusted: true,
            status: {
              code: StatusCode.ANNOTATION_SUCCESS,
              description: '',
            },
          };
        }
      }

      if (Object.keys(annotationMap).length) {
        // We first add the new annotations into the store assuming there won't be any errors...
        self._setData(self.data.concat(Object.values(annotationMap)));

        const pluralizedAnnotationText = pluralize('annotation', Object.values(annotationMap).length);

        // ...and then we persist the changes.
        try {
          for (const c of concepts) {
            const actualConcept = await self._fetchOrCreateConceptInApp(c.toObject());

            if (actualConcept) {
              annotationMap[c.getId()] = {
                ...annotationMap[c.getId()],
                data: { concepts: [actualConcept.toObject()] },
              };
            }
          }

          const response = await self.env().grpc.postAnnotations({
            appId,
            userOrOrgId: userId,
            annotations: Object.values(annotationMap).map(convertJsAnnotationToGrpcAnnotation),
          });

          if (checkIfResponseHasError(response)) {
            throw new Error(`Error while creating ${pluralizedAnnotationText}.`);
          }

          self.handleSuccessToastNotification('success', `${capitalize(pluralizedAnnotationText)} added`);
        } catch {
          self._setData(self.data.filter(({ id }) => !annotationVirtualIds.some((annotationVirtualId) => annotationVirtualId === id)));
          cogoToast.error(`There was an error while adding the ${pluralizedAnnotationText}.`);
        }
      }
    },
    async patchBoundingBoxAnnotationOptimistically(item: AnnoUiRegionItem<TIAnnotationMST>, boundingBox?: CF.API.BoundingBox) {
      const { annotation } = item;

      if (!annotation || !hasRegionsWithConcepts(annotation.data) || !isBboxShapeProps(item.regionData)) return;

      const currentUserId = self._getLoggedInUserId();
      const canvasStore = self.getCanvasStoreState();
      const isAnnotationTrusted = getIsAnnotationTrusted(annotation);

      if (!isAnnotationTrusted) {
        cogoToast.error(ERROR_MESSAGE_NON_TRUSTED);
        canvasStore.setSelectedShape(null);

        return;
      }

      // We store the current annotation in case we need to revert the changes.
      const currentAnnotation = self.data.find((a) => a.id === annotation.id);

      if (!currentAnnotation) return;

      const annotationToUpdate: CF.API.Annotations.Annotation = {
        ...annotation,
        id: annotation.id,
        data: {
          ...annotation.data,
          regions: ((annotation.data as CF.API.BaseRegionsData).regions || []).map((region) => {
            if (region.id === item.key) {
              return {
                ...region,
                region_info: {
                  bounding_box: boundingBox || (item.regionData as CF.API.BboxShapeProps).bbox,
                },
              };
            }

            return region;
          }),
        },
        worker: {
          user: {
            id: currentUserId,
          } as CF.API.Users.User,
        },
      };

      // We first optimistically update the annotation...
      self.updateAnnotationOptimistically(annotationToUpdate);

      try {
        // ...and then we send the request to the server.
        const annotations = await self.patchAnnotation(annotationToUpdate);

        if (annotations.length > 0) {
          const updatedAnnotation = annotations[0];
          const updatedRegionId = (updatedAnnotation.data as CF.API.BaseRegionsData).regions?.[0]?.id || '';

          // If the request was successful, we update the annotation with the actual data from the server.
          // This is important because if the user changed the bounding box, BE updates the region ID.
          self.updateAnnotationOptimistically(updatedAnnotation);
          // Finally, we set the selected shape to the updated region ID.
          canvasStore.setSelectedShape(updatedRegionId);
          cogoToast.success('The annotation has been updated successfully.');
        } else {
          cogoToast.info('No annotation has been updated.');
        }
      } catch {
        // If something goes wrong, we revert the changes and show an error message.
        self.updateAnnotationOptimistically(currentAnnotation);
        cogoToast.error('There was an error when updating the annotation.');
      }
    },
    async patchPolygonAnnotationOptimistically(item: AnnoUiRegionItem<TIAnnotationMST>, points?: CF.API.Polygon['points']) {
      const { appId, userId } = self.requestParams;
      // If something goes wrong, `currentAnnotations` will serve as a backup.
      const currentAnnotations = clone(self.data);
      let annotationToUpdate: CF.API.Annotations.Annotation | undefined;
      let grpcAnnotation: Annotation;

      self._setData(
        self.data.map((annotation) => {
          if (item.annotation && annotation.id === item.annotation.id) {
            const annotationData = item.annotation.data as CF.API.BaseRegionsData;

            annotationToUpdate = {
              ...item.annotation,
              data: {
                ...annotationData,
                regions: (annotationData.regions || []).map((region) => {
                  if (region.id === item.key) {
                    return {
                      ...region,
                      region_info: {
                        polygon: {
                          points: points || (item.regionData as CF.API.PolygonShapeProps).polygon.points,
                        },
                      },
                    };
                  }

                  return region;
                }),
              },
            };

            return annotationToUpdate;
          }

          return annotation;
        }),
      );

      const isAnnotationTrusted = getIsAnnotationTrusted(annotationToUpdate);
      if (annotationToUpdate && isAnnotationTrusted) {
        grpcAnnotation = convertJsAnnotationToGrpcAnnotation(annotationToUpdate);
        grpcAnnotation.setId(annotationToUpdate.id);
        grpcAnnotation.setInputId(annotationToUpdate.input_id);
        const response = await self.env().grpc.patchAnnotations({
          appId,
          userOrOrgId: userId,
          annotations: [grpcAnnotation],
          action: 'overwrite',
        });

        pipe(
          response,
          either.fold(
            () => {
              self._setData(currentAnnotations);
              cogoToast.error('There was an error while updating the annotation.');
            },
            () => {
              self.handleSuccessToastNotification('success', 'The polygon has been updated successfully!');
            },
          ),
        );
      } else {
        cogoToast.error(ERROR_MESSAGE_NON_TRUSTED);
        const canvasStore = self.getCanvasStoreState();
        if (canvasStore && canvasStore.setSelectedShape) canvasStore.setSelectedShape(null);
      }
    },
    async deleteRegionAnnotationOptimistically(annotation: CF.API.Annotations.Annotation) {
      const { appId, userId } = self.requestParams;
      const { setSelectedShape, selectedShape } = self.getCanvasStoreState();

      const isAnnotationTrusted = getIsAnnotationTrusted(annotation);
      if (!isAnnotationTrusted) {
        cogoToast.error(ERROR_MESSAGE_NON_TRUSTED);
        const canvasStore = self.getCanvasStoreState();
        if (canvasStore && canvasStore.setSelectedShape) canvasStore.setSelectedShape(null);
        return;
      }

      self._removeAnnotation(annotation);

      const annotationToRemove = {
        id: annotation.id,
        input_id: annotation.input_id,
        data: annotation.data,
      };
      const response = await self.env().grpc.patchAnnotations({
        appId,
        userOrOrgId: userId,
        annotations: [convertJsAnnotationToGrpcAnnotation(annotationToRemove)],
        action: 'remove',
        deleteIfEmptyData: true,
      });

      pipe(
        response,
        either.fold(
          () => {
            self._unshiftData([annotation]);
            if (setSelectedShape && selectedShape) setSelectedShape(selectedShape);
            cogoToast.error('There was an error while deleting the annotation.');
          },
          () => {
            self.handleSuccessToastNotification('success', 'The region annotation has been deleted successfully!');
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    resetVideoMasks() {
      self.videoMasks.replace([]);
    },
    async generateVideoMasks(currentInput: Input, videoMaskGenerationConfig: FeatureFlagsType['videoMaskGenerationConfig']): Promise<void> {
      self.setIsPredictionsLoading(true);
      const annotationsByConcept = groupFramedPointAnnotationsByConcept(self.data);

      const allVideoMasks: VideoMask[] = [];
      const grpcClient = self.env().grpc;

      for (const [conceptId, conceptGroup] of Object.entries(annotationsByConcept)) {
        try {
          const videoMaskRequest = await createVideoMaskModelRequest(conceptGroup.annotations, currentInput, videoMaskGenerationConfig);
          const response = grpcClient.generateModelOutputs(videoMaskRequest);

          const frameMasksMap: Record<string, string> = {};

          const streamHandledPromise = new Promise<void>((resolve, reject) => {
            response.on('data', async (responseData) => {
              await handleVideoMaskResponse(responseData, frameMasksMap);
            });

            response.on('end', () => {
              const trackConcept = conceptGroup.concept;

              if (trackConcept && Object.keys(frameMasksMap).length > 0) {
                allVideoMasks.push({
                  frames: frameMasksMap,
                  track: trackConcept,
                });
              }
              resolve();
            });

            response.on('error', (error) => {
              console.error(`Error generating video masks for concept ${conceptId}:`, error);
              reject(error);
            });
          });

          await streamHandledPromise;
        } catch (error) {
          console.error(`Error processing concept group ${conceptId}:`, error);
        }
      }

      self.runInAction(() => {
        if (allVideoMasks.length > 0) {
          self.videoMasks.replace(allVideoMasks);
        }
        self.setIsPredictionsLoading(false);
      });
    },
    async saveVideoMasks() {
      const { appId, userId } = self.requestParams;

      const allAnnotations = convertVideoMasksToAnnotations(self.videoMasks, self.requestParams.inputId, self._getLoggedInUserId());

      const chunks = splitAnnotationsIntoChunks(allAnnotations, SAVE_VIDEO_MASKS_BATCH_SIZE);

      const responses = [];
      const failedBatches: number[] = [];

      for (const [i, chunk] of chunks.entries()) {
        try {
          const response = await self.env().grpc.postAnnotations({
            appId,
            userOrOrgId: userId,
            annotations: chunk,
          });
          responses.push(response);
        } catch (error) {
          console.error(`Error sending batch ${i + 1}:`, error);
          failedBatches.push(i + 1);
        }
      }

      if (failedBatches.length === 0) {
        self.handleSuccessToastNotification('success', 'Video Masks saved successfully');
      } else if (failedBatches.length === chunks.length) {
        self.handleSuccessToastNotification('error', 'Failed to save any video masks');
      } else {
        self.handleSuccessToastNotification('warning', 'Video Masks partially saved.');
      }
    },
  }))
  .actions((self) => ({
    async acceptPredictionsOptimistically() {
      // Handle region suggestions
      Array.from(self.regionAnnotationMap.values()).forEach((entry) => {
        if (!entry) return;
        entry.regionMap.forEach((annotations) => {
          annotations.forEach((annotation) => {
            const annotationData = annotation.data;
            if (annotation.isSuggestion && hasRegionsWithConcepts(annotationData) && annotationData.regions?.length) {
              self.createRegionAnnotationsOptimistically(annotation);
            }
          });
        });
      });

      // Handle classification suggestions
      Array.from(self.classificationAnnotationMap.values()).forEach((annotations) => {
        annotations.forEach((annotation) => {
          const annotationData = annotation.data;
          if (annotation.isSuggestion && hasConcepts(annotationData) && annotationData.concepts?.length) {
            self.createClassificationAnnotationOptimistically(...annotationData.concepts.map(convertJsConceptToGrpcConcept));
          }
        });
      });
    },
    patchRegionAnnotationOptimistically(item: AnnoUiRegionItem<TIAnnotationMST>, concept: CF.API.Concept) {
      if (item.annotation) {
        const regionData = item.annotation.data as CF.API.BaseRegionsData;
        const region = regionData.regions?.[0];
        let regionItem;

        if (region) {
          regionItem = {
            ...item,
            annotation: {
              ...item.annotation,
              data: {
                ...item.annotation.data,
                regions: [
                  {
                    ...region,
                    data: {
                      ...region.data,
                      concepts: [concept],
                    },
                  },
                ],
              },
            },
          };

          if ('bbox' in regionItem.regionData) {
            self.patchBoundingBoxAnnotationOptimistically(regionItem);
          } else if ('polygon' in regionItem.regionData) {
            self.patchPolygonAnnotationOptimistically(regionItem);
          }
        }
      }
    },
  }))
  .actions((self) => ({
    async deleteAnnotationOptimistically(concept: CF.API.ConceptPartial, annotationIds: string[]) {
      const { appId } = self.requestParams;
      const currentUserId = self._getLoggedInUserId();
      const annotationBackupArray = clone(self.data);

      const nonMatchingAnnotations: CF.API.Annotations.Annotation[] = [];
      const nonTrustedAnnotations: CF.API.Annotations.Annotation[] = [];
      const tempAnnotations: CF.API.Annotations.Annotation[] = [];
      const annotationsToUpdate: CF.API.Annotations.Annotation[] = [];
      const annotationsToDelete: CF.API.Annotations.Annotation[] = [];

      for (const annotation of self.data) {
        if (annotation.isTemporary) {
          tempAnnotations.push(annotation);
          continue;
        }

        if (!annotationIds.includes(annotation.id)) {
          nonMatchingAnnotations.push(annotation);
          continue;
        }

        const isTrusted = getIsAnnotationTrusted(annotation);

        if (isTrusted) {
          const annotationConcepts = getConceptsFromAnnotationData(annotation.data);
          const remainingConcepts = annotationConcepts.filter((c) => c.id !== concept?.id);

          // Some annotations are associated with multiple concepts. In such cases, we avoid deleting the entire annotation
          // to prevent removing other valid concepts unintentionally.
          if (remainingConcepts.length) {
            const updatedAnnotation = updateAnnotationConcepts(annotation, remainingConcepts);

            if (updatedAnnotation) {
              annotationsToUpdate.push(updatedAnnotation);
            }
          } else {
            annotationsToDelete.push(annotation);
          }
        } else {
          nonTrustedAnnotations.push(annotation);
        }
      }

      // Optimistically update the UI
      self._setData([...nonMatchingAnnotations, ...nonTrustedAnnotations, ...annotationsToUpdate, ...tempAnnotations]);

      try {
        const grpc = self.env().grpc;
        let response;

        if (annotationsToUpdate.length > 0) {
          response = await grpc.patchAnnotations({
            appId,
            userOrOrgId: currentUserId,
            annotations: annotationsToUpdate.map(convertJsAnnotationToGrpcAnnotation),
            action: 'overwrite',
          });
        }

        if (annotationsToDelete.length > 0) {
          response = await grpc.patchAnnotations({
            appId,
            userOrOrgId: currentUserId,
            annotations: annotationsToDelete.map(convertJsAnnotationToGrpcAnnotation),
            action: 'remove',
            deleteIfEmptyData: true,
          });
        }

        if (response && checkIfResponseHasError(response)) {
          throw new Error('Error while deleting annotations.');
        }

        self.handleSuccessToastNotification('success', `The ${pluralize('annotation', annotationIds.length)} has been deleted successfully!`);
      } catch {
        self._setData(annotationBackupArray);
        cogoToast.error(`There was an error while deleting the ${pluralize('annotation', annotationIds.length)}.`);
      }
    },

    async deleteSingleRegionAnnotationOptimistically(annotationToRemove: CF.API.Annotations.Annotation) {
      const userId = self._getLoggedInUserId();

      const foundTrustedAnnotation = self.data.find(
        (annotation) =>
          annotation.id === annotationToRemove.id && annotationToRemove.worker?.user?.id === userId && getIsAnnotationTrusted(annotation),
      );
      if (!foundTrustedAnnotation) {
        cogoToast.error(ERROR_MESSAGE_NON_TRUSTED);
        const canvasStore = self.getCanvasStoreState();
        if (canvasStore && canvasStore.setSelectedShape) canvasStore.setSelectedShape(null);
        return;
      }

      // We backup the current annotation array for rollback in case of an error.
      const annotationBackupArray = clone(self.data);
      const ERROR_MESSAGE = 'There was an error while deleting the annotation.';

      // We optimistically remove the annotation from the data.
      self._setData(self.data.filter((annotation) => annotation.id !== foundTrustedAnnotation.id));

      const { appId, userId: userOrOrgId } = self.requestParams;

      try {
        const response = await self.env().grpc.patchAnnotations({
          appId,
          userOrOrgId,
          annotations: [convertJsAnnotationToGrpcAnnotation(foundTrustedAnnotation)],
          action: 'remove',
          deleteIfEmptyData: true,
        });

        pipe(
          response,
          either.fold(
            () => self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE),
            (r) => {
              if (r.getStatus()?.getCode() === StatusCode.SUCCESS) {
                self.handleSuccessToastNotification('success', 'The annotation has been deleted successfully.');
              } else {
                self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE);
              }
            },
          ),
        );
      } catch {
        self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE);
      }
    },
    async deleteAllAnnotationsOfSingleConceptOptimistically(annotationIds: string[]) {
      const foundAnnotations = self.data.filter((annotation) => annotationIds.includes(annotation.id));
      if (!foundAnnotations.length) return;

      const { trusted: trustedAnnotations, nonTrusted } = partitionAnnotationsByTrust(foundAnnotations);

      if (nonTrusted.length !== 0) {
        cogoToast.error(trustedAnnotations.length === 0 ? ERROR_MESSAGE_NON_TRUSTED : ERROR_MESSAGE_NON_TRUSTED_PARTIALLY);
        const canvasStore = self.getCanvasStoreState();
        if (canvasStore && canvasStore.setSelectedShape) canvasStore.setSelectedShape(null);
      }

      if (trustedAnnotations.length === 0) return;

      const trustedAnnotationIds = trustedAnnotations.map((annotation) => annotation && annotation.id).filter((a) => !!a);

      // We backup the current annotation array for rollback in case of an error.
      const annotationBackupArray = clone(self.data);
      const ERROR_MESSAGE = 'There was an error while deleting the annotations.';

      // We optimistically remove the annotation from the data.
      self._setData(self.data.filter((annotation) => !trustedAnnotationIds.includes(annotation.id)));

      const { appId, userId: userOrOrgId } = self.requestParams;

      const annotations = trustedAnnotations.map(convertJsAnnotationToGrpcAnnotation);
      try {
        const response = await self.env().grpc.patchAnnotations({
          appId,
          userOrOrgId,
          annotations,
          action: 'remove',
          deleteIfEmptyData: true,
        });

        pipe(
          response,
          either.fold(
            () => self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE),
            (r) => {
              if (r.getStatus()?.getCode() === StatusCode.SUCCESS) {
                self.handleSuccessToastNotification('success', 'The annotations has been deleted successfully.');
              } else {
                self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE);
              }
            },
          ),
        );
      } catch {
        self._rollbackAnnotations(annotationBackupArray, ERROR_MESSAGE);
      }
    },

    // TODO: We need to refactor the actions! Currently, we have several actions that handle similar logic for different scenarios.
    // Ideally, each action should follow the single-responsibility principle, doing one thing well.
    // We can then compose these smaller, focused actions to handle complex workflows more cleanly and maintainably.
    async deleteAnnotations(annotations: CF.API.Annotations.Annotation[], isOwner?: boolean) {
      const userId = self._getLoggedInUserId();
      const annotationsToDelete = isOwner ? annotations : annotations.filter((annotation) => annotation.worker?.user?.id === userId);

      this.deleteAllAnnotationsOfSingleConceptOptimistically(annotationsToDelete.map((annotation) => annotation.id));
    },

    async createConceptRelation(suggestedConcept: CF.API.ConceptPartial, newConceptName: string) {
      const { grpc } = self.env();
      const { appId } = self.requestParams;
      const currentUserId = self._getLoggedInUserId();
      // We make a backup copy of the concept relation map to restore it if an error occurs.
      const conceptRelationMapBkp = clone(self.conceptRelationMap);
      let hasError = false;

      try {
        const relations = conceptRelationMapBkp.get(suggestedConcept.name) || [];
        const objectConcept = new Concept();
        const predicate = 'synonym';

        const { name, id } = createValidConceptNameIdSet(newConceptName);
        objectConcept.setId(id);
        objectConcept.setName(name);
        objectConcept.setAppId(appId);

        // Then, we optimistically add the new relation to our internal map to prevent the user from waiting.
        const newRelation: CF.API.Concepts.ConceptRelation = {
          id: makeUID(),
          subjectConcept: suggestedConcept,
          objectConcept: grpcToRestConcept(objectConcept),
          predicate,
        };

        self.conceptRelationMap.set(suggestedConcept.name, [newRelation]);

        // After optimistically adding the new relation, we proceed to hit BE to persist the changes.
        // So, first we check if both the suggested and the new concept exist, and if not, we create them.
        const fetchedSuggestedConcept = await createConcept({
          request: {
            userOrOrgId: currentUserId,
            appId,
          },
          concept: {
            id: suggestedConcept.id,
            name: suggestedConcept.name,
            app_id: appId,
          },
        });
        const fetchedObjectConcept = await createConcept({
          request: {
            userOrOrgId: currentUserId,
            appId,
          },
          concept: {
            id: objectConcept.getId(),
            name: objectConcept.getName(),
            app_id: appId,
          },
        });

        if (fetchedSuggestedConcept && fetchedObjectConcept) {
          // And then we create the new concept relation.
          const postConceptRelationsResponse = await grpc.postConceptRelations({
            appId,
            userOrOrgId: currentUserId,
            id: newRelation.id,
            conceptId: suggestedConcept.id,
            objectConcept,
            predicate,
          });

          hasError = checkIfResponseHasError(postConceptRelationsResponse);
        } else {
          hasError = true;
        }

        // Delete any previous relations as each concept can have only one relation
        if (relations.length) {
          const relationIds = relations.map((relation) => relation.id);

          await grpc.deleteConceptRelations({
            appId,
            userOrOrgId: currentUserId,
            conceptId: suggestedConcept.id,
            relationIds,
          });
        }
      } catch {
        hasError = true;
      } finally {
        if (hasError) {
          // If there was some kind of error, we roll back the concept relation map to its initial state.
          self._setConceptRelationMap(conceptRelationMapBkp);
          cogoToast.error('There was an error while creating the new vocab.');
        } else {
          self.handleSuccessToastNotification('success', 'The new vocab has been created successfully.');
        }
      }
    },
    async createRegionAnnotationFromPrediction(item: AnnoUiRegionItem) {
      const { inputId, appId } = self.requestParams;
      const { key: regionId, annotation } = item;
      const userId = self._getLoggedInUserId();

      if (!annotation) return;

      const annotationToCreate: CF.API.Annotations.Annotation = {
        id: makeUID(16),
        input_id: inputId,
        data: {
          regions: [
            {
              id: regionId,
              region_info: determineRegionInfo(item.regionData),
              data: {
                concepts: (item.concepts || []).map((c) => ({
                  ...c,
                  id: item.annotation?.isSuggestion ? c.name : c.id,
                  name: c.name,
                  app_id: appId,
                })),
              },
            },
          ],
        },
        trusted: true,
        isSuggestion: false,
        isTemporary: false,
        worker: {
          user: {
            id: userId,
          } as CF.API.Users.User,
        },
        status: {
          code: StatusCode.ANNOTATION_SUCCESS,
          description: '',
        },
      };

      // We first optimistically append the new annotation to the list of annotations...
      self.appendAnnotationOptimistically(annotationToCreate);

      // ...and then we try to persist the changes.
      try {
        await self.postAnnotation(annotationToCreate);
        cogoToast.success('The annotation has been created successfully.');
      } catch {
        // If there is an error, we roll back the data to its previous state.
        self.removeAnnotationOptimistically(annotationToCreate);
        cogoToast.error('There was an error while creating the annotation.');
      }
    },
  }))
  .views((self) => ({
    get getAnnotationsToMoveTo() {
      const concept = (self.annotationToMoveFrom?.data as CF.API.BaseRegionsData)?.regions?.[0]?.data?.concepts?.[0];
      if (!concept?.name) return [];

      const regionConcepts = Array.from(self.regionAnnotationMap.keys()).reduce((result, key) => {
        // Because keys in the map correspond to concept names used for grouping annotations.
        if (key !== concept.name) {
          result.add(key);
        }

        return result;
      }, new Set<string>());

      return [...regionConcepts];
    },
  }))
  .actions((self) => ({
    setAnnotationToMove(annotation: CF.API.Annotations.Annotation | null) {
      self.annotationToMoveFrom = cast(annotation);
    },
    handleMoveAnnotation(newConcept: string) {
      if (!newConcept || !self.annotationToMoveFrom) return;

      const newConceptForAnnotation = self.regionAnnotationMap.get(newConcept)?.concept;

      if (!newConceptForAnnotation) return;

      const data = {
        regions: [
          {
            id: makeUID(),
            region_info: (self.annotationToMoveFrom.data as CF.API.BaseRegionsData)?.regions?.[0].region_info || ({} as CF.API.RegionInfo),
            data: { concepts: newConceptForAnnotation ? [newConceptForAnnotation] : [] },
          },
        ],
      };

      const newAnnotation = {
        data,
        id: makeUID(),
        input_id: self.requestParams.inputId,
        worker: {
          user: {
            id: self.requestParams.userId,
          } as CF.API.Users.User,
        },
      };

      self.deleteSingleRegionAnnotationOptimistically(self.annotationToMoveFrom);
      self.createRegionAnnotationOptimistically(newAnnotation);
    },
    countClassificationMapEntries() {
      // We manually count entries in classificationAnnotationMap, ignoring empty arrays used to preserve insertion order.
      return Array.from(self.classificationAnnotationMap.values()).reduce((counter, annotations) => {
        if (annotations.length > 0) {
          counter += 1;
        }

        return counter;
      }, 0);
    },
  }))
  // ==============================================================================
  // Reactions
  // ==============================================================================
  .actions((self) => {
    const afterCreate = () => {
      // Creates `classificationAnnotationMap` based on annotation data.
      reaction<[CF.API.Annotations.Annotation[], string]>(
        () => [toJS(self._filteredAnnotations), self.annotationSearchText],
        ([annotations, searchText]) => {
          runInAction(() => {
            // Ideally, this filtering should be handled in the UI, but it's more efficient to apply it here while constructing the map,
            // so we avoid the overhead of rebuilding the map later.
            const matchSearchText = (conceptKey: string) => (searchText ? new RegExp(searchText, 'i').test(conceptKey) : true);

            const map = new Map<string, CF.API.Annotations.Annotation[]>();

            // As a product requirement, if an annotation ever existed, we need to keep showing a placeholder
            // even after the user deletes the last one. To handle this, we set temporary annotations
            // based on real annotation data.
            for (const [conceptKey, annotationList] of self.classificationAnnotationMap.entries()) {
              const hasRealAnnotation = annotationList.some((annotation) => !annotation.isSuggestion);

              if (hasRealAnnotation && matchSearchText(conceptKey) && self._areAnnotationFiltersEmpty) {
                const conceptName = conceptKey.split(NON_TRUSTED_ANNOTATION_SEPARATOR)[0];
                const concept = getConceptFromAnnotations(conceptName, annotationList);
                const tempAnnotation = createTempClassificationAnnotation(concept);

                if (tempAnnotation) {
                  map.set(conceptKey, [AnnotationMST.create(tempAnnotation)]);
                }
              } else {
                // We set the entry to an empty array instead of deleting it to preserve the insertion order.
                map.set(conceptKey, []);
              }
            }

            // After that, we iterate through real annotation data and add them to the map.
            for (const annotation of annotations) {
              // If the annotation is not a classification annotation, we skip it.
              if (!hasConcepts(annotation.data)) continue;

              const concepts = annotation.data.concepts ?? [];

              concepts.forEach((concept) => {
                // We use a different map key depending on whether the concept is trusted or untrusted.
                const conceptKey = getClassificationMapEntryKey(concept, annotation);

                if (!matchSearchText(conceptKey)) return;

                const currentAnnotations: CF.API.Annotations.Annotation[] = map.get(conceptKey) || [];
                const annotationWithSingleConcept = {
                  ...annotation,
                  data: {
                    concepts: [concept],
                  },
                };
                const test = AnnotationMST.create(annotationWithSingleConcept);

                map.set(conceptKey, currentAnnotations.concat(test));
              });
            }

            // Finally, we set the changes to `classificationAnnotationMap`.
            self.classificationAnnotationMap.clear();
            for (const [key, value] of map.entries()) {
              self.classificationAnnotationMap.set(key, value);
            }
          });
        },
      );

      // Keeps in sync `classificationAnnotationMap` with the concept selector from the classification pane.
      reaction(
        () => toJS(self.classificationAnnotationMap),
        (classificationAnnotationMap) => {
          const conceptList: Concept[] = [];

          for (const annotations of classificationAnnotationMap.values()) {
            annotations.forEach((annotation) => {
              if (annotation.isSuggestion) return;

              const concepts = getConceptsFromAnnotationData(annotation.data);

              concepts.forEach((concept) => {
                conceptList.push(convertJsConceptToGrpcConcept(concept));
              });
            });
          }

          self.setClassificationPaneConcepts(conceptList);
        },
      );

      // Creates `regionAnnotationMap` based on annotation data.
      reaction<[CF.API.Annotations.Annotation[], string]>(
        () => [toJS(self._filteredAnnotations), self.annotationSearchText],
        ([annotations, searchText]) => {
          // Ideally, this filtering should be handled in the UI, but it's more efficient to apply it here while constructing the map,
          // so we avoid the overhead of rebuilding the map later.
          const matchSearchText = (conceptKey: string) => (searchText ? new RegExp(searchText, 'i').test(conceptKey) : true);

          const map: RegionAnnotationMap = new Map();

          // After that, we iterate through real annotation data and add them to the map.
          for (const annotation of annotations) {
            if (!hasRegionsWithConcepts(annotation.data)) continue;

            const regions = annotation.data.regions ?? [];

            regions.forEach((region) => {
              const concepts = region.data.concepts ?? [];

              concepts.forEach((concept) => {
                if (!matchSearchText(concept.name)) return;

                // This variable tries to normalize the complex data structure sent by BE, which allows an annotation to have different types of
                // regions, even if those regions correspond to entirely different concepts (e.g., "cat" and "bed"). To simplify this for the FE,
                // we recreate the annotation while preserving its original structure, but with an important distinction:
                // there's a 1:1 relationship between the annotation, the region, and the concept.
                // If there are two regions with different concepts that "share" the same annotation, they will be represented as two separate
                // annotations. There won't be ID collisions, as the ID used is a combination of the annotation ID, region ID and concept ID.
                // (for more info, please refer to the `generateInternalAnnotationId()` function).
                const internalId = generateInternalAnnotationId({
                  id: annotation.id || '',
                  regionId: region.id || '',
                  conceptId: concept.id || '',
                  worker: annotation.worker,
                  taskId: annotation?.task_id || '',
                });

                const annotationToAdd: CF.API.Annotations.Annotation = {
                  ...annotation,
                  internalId,
                  data: {
                    ...annotation.data,
                    regions: [
                      {
                        ...region,
                        data: {
                          ...region.data,
                          concepts: [concept],
                        },
                      },
                    ],
                  },
                };

                const conceptName = concept.name;
                const mapEntryValue = map.get(conceptName) || { concept, regionMap: new Map() };
                const mapKey = getRegionMapEntryKey(region, annotation);
                const currentRegionMap = mapEntryValue.regionMap;
                const currentAnnotations = currentRegionMap.get(mapKey) || [];

                mapEntryValue.regionMap.set(mapKey, [...currentAnnotations, annotationToAdd]);
                map.set(conceptName, mapEntryValue);
              });
            });
          }

          // Finally, we set the changes to `regionAnnotationMap`.
          self.regionAnnotationMap.replace(map);
        },
      );

      // Keeps in sync `regionAnnotationMap` with the concept selector from the region pane.
      reaction(
        () => toJS(self.regionAnnotationMap),
        (regionAnnotationMap) => {
          const conceptList: Concept[] = [];

          for (const mapEntry of regionAnnotationMap.values()) {
            if (!mapEntry) continue;
            const { concept, regionMap } = mapEntry;

            if (regionMap.size === 0) {
              // If the region map is empty, it's likely because the user selected the concept from the selector
              // or the concept was pinned and came from the local storage.
              conceptList.push(convertJsConceptToGrpcConcept(concept));
            } else {
              mapEntry.regionMap.forEach((annotations) => {
                annotations.forEach((annotation) => {
                  if (annotation.isSuggestion) return;

                  const concepts = getConceptsFromAnnotationData(annotation.data);

                  concepts.forEach((c) => {
                    conceptList.push(convertJsConceptToGrpcConcept(c));
                  });
                });
              });
            }
          }

          self.setRegionPaneConcepts(conceptList);
        },
      );
    };

    return { afterCreate };
  });

export function createAnnotationsStore(...args: Parameters<typeof AnnotationsStore.create>): ReturnType<typeof AnnotationsStore.create> {
  const modelInstance = AnnotationsStore.create(...args);
  return modelInstance;
}

export type TIAnnotationStore = ReturnType<typeof createAnnotationsStore>;

function showCreationError(status?: CF.API.Status): void {
  cogoToast.error(status?.description || 'Please try again', { heading: status?.details || 'Annotation creation failed.' });
}
/* eslint-enable no-continue */
